import { useElements, useStripe } from "@stripe/react-stripe-js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CreditCard, TriangleAlert } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

import { stripeUrl, supportURL } from "common/const";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import { PermissionsWrapper } from "components/permissions/permissions";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { addPaymentMethod } from "data/mutations-common";
import { fetchPaymentMethod, fetchSubscription } from "data/queries-common";
import { resolveIdentityId } from "utils/identity";

import { EditPaymentMethodDialogue } from "./editPaymentDialogue";

interface PaymentMethodInfoProps {
  setPaymentMethod?: Dispatch<SetStateAction<boolean>>;
  hideTitle?: boolean;
}

const PaymentMethodInfo = (props: PaymentMethodInfoProps) => {
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const stripe = useStripe();
  const stripeElements = useElements();
  const queryClient = useQueryClient();
  const [editingCC, setEditingCC] = useState(false);

  const paymentMethodQuery = useQuery({
    queryKey: [QueryKeys.paymentMethod, identityId],
    queryFn: () => fetchPaymentMethod(),
  });

  const subscriptionQuery = useQuery({
    queryKey: [QueryKeys.subscription, identityId],
    queryFn: () => fetchSubscription(),
  });

  useEffect(() => {
    if (
      paymentMethodQuery.isSuccess &&
      paymentMethodQuery.data &&
      props.setPaymentMethod
    ) {
      props.setPaymentMethod(true);
    }
  }, [paymentMethodQuery.isSuccess]);

  const paymentMethodMutation = useMutation({
    mutationFn: () => addPaymentMethod(stripe, stripeElements, setEditingCC),
    onSuccess: (newPaymentMethod) => {
      queryClient.setQueryData(
        [QueryKeys.paymentMethod, identityId],
        newPaymentMethod,
      );
    },
  });

  const handleOnClose = () => {
    paymentMethodMutation.reset();
    setEditingCC(false);
  };

  return (
    <>
      <Card>
        {!props.hideTitle && (
          <CardHeader>
            <CardTitle>Payment Method</CardTitle>
          </CardHeader>
        )}
        <CardContent className="space-y-2">
          <ReactQueryErrorWrapper
            queries={[paymentMethodQuery, subscriptionQuery]}
            mutations={[paymentMethodMutation]}
          />

          {paymentMethodQuery.isPending && (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}

          {paymentMethodQuery.isSuccess && paymentMethodQuery.data && (
            <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2">
              <div className="flex min-w-0 items-center gap-2">
                <CreditCard className="size-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    •••• {paymentMethodQuery.data.last4}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Expires {String(paymentMethodQuery.data.expireMonth).padStart(2, "0")}/
                    {paymentMethodQuery.data.expireYear}
                  </p>
                </div>
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">
                Securely stored
              </span>
            </div>
          )}

          {paymentMethodQuery.isSuccess && paymentMethodQuery.data === null && (
            <Alert variant="warning" className="py-2">
              <TriangleAlert className="size-4" />
              <AlertDescription>
                No card added. Add one to enable overage payments.
              </AlertDescription>
            </Alert>
          )}

          {subscriptionQuery.isPending && <Skeleton className="h-9 w-32" />}

          <PermissionsWrapper
            keys={[PermissionsKeys.paymentMethodUpdate]}
            messages={{
              [PermissionsReasons.EXTERNALLY_MANAGED]: (
                <Alert variant="info">
                  <AlertDescription>
                    Your subscription is managed outside of hub. Please contact{" "}
                    <Link
                      className="underline"
                      target="_blank"
                      to={{ pathname: supportURL }}
                    >
                      support
                    </Link>{" "}
                    to change your payment method.
                  </AlertDescription>
                </Alert>
              ),
            }}
          >
            <Button
              size="sm"
              variant={paymentMethodQuery.data ? "outline" : "default"}
              onClick={() => setEditingCC(true)}
            >
              {paymentMethodQuery.data ? "Change card" : "Add card"}
            </Button>
          </PermissionsWrapper>
        </CardContent>
      </Card>

      <EditPaymentMethodDialogue
        open={editingCC}
        handleOnClose={handleOnClose}
        stripeUrl={stripeUrl}
        paymentMethodMutation={paymentMethodMutation}
      />
    </>
  );
};

export default PaymentMethodInfo;
